import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Player, Team, Game, Round, Match } from '@/types';

// Define the database schema
interface BelotDB extends DBSchema {
  players: {
    key: string;
    value: Player;
    indexes: { 'by-name': string };
  };
  teams: {
    key: string;
    value: Team;
    indexes: { 'by-name': string };
  };
  matches: {
    key: string;
    value: Match;
    indexes: { 'by-status': string; 'by-created': Date };
  };
  games: {
    key: string;
    value: Game;
    indexes: { 'by-status': string; 'by-created': Date; 'by-match': string };
  };
  rounds: {
    key: string;
    value: Round;
    indexes: { 'by-game': string };
  };
}

const DB_NAME = 'belot-pwa';
const DB_VERSION = 3; // Bumped for trump support in rounds

let dbInstance: IDBPDatabase<BelotDB> | null = null;
let dbInitPromise: Promise<IDBPDatabase<BelotDB>> | null = null;

/**
 * Initialize and get the database instance
 */
export async function initDB(): Promise<IDBPDatabase<BelotDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  // Prevent multiple simultaneous initialization attempts
  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = openDB<BelotDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      // Migration from v2 to v3: Rounds now have trumpSuit and trumpCallerId fields
      // Existing rounds without these fields will be handled gracefully in roundRepo
      if (oldVersion === 2) {
        // No schema changes needed - Round entity just has new optional fields
        // Repository will handle missing trump fields in existing data
      }

      // Migration from v1 to v2: Clear old games/rounds data (they don't have matchId)
      if (oldVersion === 1) {
        // Clear old games and rounds that don't have matchId
        if (db.objectStoreNames.contains('games')) {
          transaction.objectStore('games').clear();
        }
        if (db.objectStoreNames.contains('rounds')) {
          transaction.objectStore('rounds').clear();
        }
      }

      // Players store
      if (!db.objectStoreNames.contains('players')) {
        const playerStore = db.createObjectStore('players', { keyPath: 'id' });
        playerStore.createIndex('by-name', 'name');
      }

      // Teams store
      if (!db.objectStoreNames.contains('teams')) {
        const teamStore = db.createObjectStore('teams', { keyPath: 'id' });
        teamStore.createIndex('by-name', 'name');
      }

      // Matches store (new in v2)
      if (!db.objectStoreNames.contains('matches')) {
        const matchStore = db.createObjectStore('matches', { keyPath: 'id' });
        matchStore.createIndex('by-status', 'status');
        matchStore.createIndex('by-created', 'createdAt');
      }

      // Games store - recreate if upgrading to add by-match index
      if (oldVersion < 2 && db.objectStoreNames.contains('games')) {
        db.deleteObjectStore('games');
      }
      if (!db.objectStoreNames.contains('games')) {
        const gameStore = db.createObjectStore('games', { keyPath: 'id' });
        gameStore.createIndex('by-status', 'status');
        gameStore.createIndex('by-created', 'createdAt');
        gameStore.createIndex('by-match', 'matchId');
      }

      // Rounds store
      if (!db.objectStoreNames.contains('rounds')) {
        const roundStore = db.createObjectStore('rounds', { keyPath: 'id' });
        roundStore.createIndex('by-game', 'gameId');
      }
    },
  });

  dbInstance = await dbInitPromise;
  return dbInstance;
}

/**
 * Get the database instance - auto-initializes if needed
 * Note: This returns the instance synchronously if already initialized,
 * or throws if called before any async initialization has completed.
 * For safety, prefer using getDBAsync() in async contexts.
 */
export function getDB(): IDBPDatabase<BelotDB> {
  if (!dbInstance) {
    throw new Error('Database not initialized. Call initDB() first.');
  }
  return dbInstance;
}

/**
 * Get the database instance asynchronously - ensures DB is initialized
 */
export async function getDBAsync(): Promise<IDBPDatabase<BelotDB>> {
  if (dbInstance) {
    return dbInstance;
  }
  return initDB();
}

/**
 * Close the database connection
 */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

/**
 * Clear all data from the database
 */
export async function clearAllData(): Promise<void> {
  const db = await getDBAsync();

  const tx = db.transaction(
    ['players', 'teams', 'matches', 'games', 'rounds'],
    'readwrite'
  );

  await Promise.all([
    tx.objectStore('players').clear(),
    tx.objectStore('teams').clear(),
    tx.objectStore('matches').clear(),
    tx.objectStore('games').clear(),
    tx.objectStore('rounds').clear(),
    tx.done,
  ]);
}

export type { BelotDB };
